"use client";

import { type ReactNode, useEffect } from "react";
import { AuthProvider } from "@/contexts/auth-context";
import { ToastProvider } from "@/contexts/toast-context";
import { ConfirmProvider } from "@/contexts/confirm-context";
import { SWRProvider } from "@/lib/swr-provider";
import { installErrorReporter } from "@/lib/error-reporter";
import { initAnalytics } from "@/lib/analytics";
import { rememberClickIds, rememberFirstTouch } from "@/lib/signup-funnel";
import { rememberComposerDeepLink } from "@/lib/composer-deep-link";
import { AppUpdateBanner } from "@/components/app-update-banner";

export function Providers({ children }: { children: ReactNode }) {
  // Route browser errors (logger hook + global window listeners) to the
  // backend Cloud Error Reporting sink. Idempotent; runs once on mount.
  // The same first-paint hook stashes any Google Ads click id on the landing
  // URL so a signup days later can still carry it (ENG-5227), and any
  // composer deep link so it survives the signup detour (ENG-5231).
  useEffect(() => {
    installErrorReporter();
    initAnalytics();
    rememberClickIds();
    rememberFirstTouch();
    rememberComposerDeepLink();
  }, []);

  return (
    <SWRProvider>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <AppUpdateBanner />
            {children}
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </SWRProvider>
  );
}
